import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  HardHat, 
  CheckCircle2, 
  AlertTriangle,
  Phone,
  ArrowLeft,
  Ruler,
  Truck,
  Shovel,
  Building,
  Shield,
  FileCheck,
  Users,
  AlertCircle,
  Scale
} from "lucide-react";

const constructionRisks = [
  {
    icon: Ruler,
    title: "עבודה בגובה",
    description: "פיגומים, סולמות, גגות, פתחים - הגורם המוביל לתאונות קטלניות",
    regulations: [
      "תקנות הבטיחות בעבודה בגובה",
      "הכשרת עובדים חובה",
      "בדיקות ציוד תקופתיות"
    ]
  },
  {
    icon: Truck,
    title: "ציוד מכני הנדסי",
    description: "מנופים, עגורנים, מחפרונים, מלגזות - סיכון גבוה",
    regulations: [
      "הסמכת מפעילים",
      "בדיקות שנתיות",
      "נהלי הפעלה בטוחים"
    ]
  },
  {
    icon: Shovel,
    title: "חפירות ותעלות",
    description: "סכנת קריסה, התמוטטות דפנות, חנק",
    regulations: [
      "תמיכת דפנות חובה",
      "מרחקי בטיחות",
      "תכנון הנדסי"
    ]
  },
  {
    icon: Building,
    title: "מבנים זמניים",
    description: "טפסות, פיגומים, גידורים - חובת יציבות ובטיחות",
    regulations: [
      "תכניות הנדסיות",
      "בדיקות יציבות",
      "אישורי מהנדס"
    ]
  },
  {
    icon: AlertTriangle,
    title: "נפילת חפצים",
    description: "חומרי בנייה, כלים, פסולת - סיכון לעוברים ושבים",
    regulations: [
      "רשתות הגנה",
      "אזורי סגירה",
      "קסדות חובה"
    ]
  },
  {
    icon: Shield,
    title: "חשמל באתר",
    description: "חיבורים זמניים, לחות, כבלים חשופים",
    regulations: [
      "לוחות חשמל מאושרים",
      "בדיקות תקופתיות",
      "הארקה תקינה"
    ]
  }
];

const constructionServices = [
  {
    title: "ליווי פרויקטים",
    description: "ממונה בטיחות צמוד לאתר לאורך כל שלבי הבנייה",
    includes: ["נוכחות קבועה באתר", "פיקוח יומיומי", "דוחות שבועיים"]
  },
  {
    title: "הכנה לביקורות",
    description: "הכנת האתר לביקורות משרד העבודה ורשויות",
    includes: ["בדיקה מקדימה", "תיקון ליקויים", "ליווי הביקורת"]
  },
  {
    title: "הדרכות עובדים",
    description: "הדרכות בטיחות לכל עובדי האתר",
    includes: ["הדרכת כניסה לאתר", "עבודה בגובה", "ציוד מגן"]
  },
  {
    title: "תכניות בטיחות",
    description: "כתיבת תכניות בטיחות לפרויקט",
    includes: ["תכנית אב לבטיחות", "נהלי עבודה", "תכנית חירום"]
  }
];

/**
 * דרישות החוק המדויקות לאתרי בנייה
 * מבוסס על תקנות ממונים על הבטיחות התשנ"ו-1996 וחוק ארגון הפיקוח על העבודה
 */
const legalRequirements = [
  {
    title: "ממונה בטיחות - 100 עובדים",
    details: "מבצע בנייה (קבלן ראשי) חייב במינוי ממונה בטיחות כאשר מספר העובדים באתר מגיע ל-100 ומעלה (תקנה 4 לתקנות התשנ\"ו)",
    icon: Users,
    critical: true
  },
  {
    title: "עוזר בטיחות - אתרים גדולים",
    details: "אתר בנייה מעל 7 מטר גובה ומעל 1,000 מ\"ר שטח - חייב במינוי עוזר בטיחות (סעיף 25ב לחוק - הוראת שעה)",
    icon: HardHat,
    critical: true
  },
  {
    title: "מנהל עבודה מוסמך",
    details: "חובה למנות מנהל עבודה מוסמך בכל אתר בנייה - לפי תקנות הבטיחות בעבודה (עבודות בנייה) התשמ\"ח-1988",
    icon: Scale,
    critical: true
  },
  {
    title: "תכנית בטיחות לאתר",
    details: "חובה להכין תכנית בטיחות מקיפה לאתר לפני תחילת העבודות",
    icon: FileCheck,
    critical: false
  },
  {
    title: "הדרכות עובדים",
    details: "כל עובד חייב לעבור הדרכת בטיחות לפני תחילת העבודה באתר לפי תקנות מסירת מידע התשנ\"ט-1999",
    icon: Users,
    critical: false
  },
  {
    title: "ציוד מגן אישי",
    details: "חובה לספק ולאכוף שימוש בציוד מגן אישי מתאים לכל עובד באתר",
    icon: Shield,
    critical: false
  }
];

const faqs = [
  {
    question: "מתי נדרש ממונה בטיחות באתר בנייה?",
    answer: "לפי תקנה 4 לתקנות ממונים על הבטיחות התשנ\"ו-1996, מבצע בנייה (קבלן ראשי) חייב במינוי ממונה בטיחות כאשר מספר העובדים באתר מגיע ל-100 ומעלה. בנוסף, באתרי משנה של קבלני משנה עם 50 עובדים ומעלה - יש צורך בממונה נפרד."
  },
  {
    question: "מתי נדרש עוזר בטיחות באתר בנייה?",
    answer: "לפי סעיף 25ב לחוק ארגון הפיקוח על העבודה (הוראת שעה מתיקון 11+12), אתר בנייה שבו נבנה מבנה מעל 7 מטר גובה ומעל 1,000 מ\"ר שטח - חייב במינוי עוזר בטיחות. שני התנאים צריכים להתקיים."
  },
  {
    question: "מה ההבדל בין מנהל עבודה לממונה בטיחות?",
    answer: "מנהל עבודה מוסמך נדרש בכל אתר בנייה ואחראי על ביצוע העבודות וקידום הפרויקט. ממונה בטיחות אחראי על הבטיחות באתר ונדרש באתרים עם 100 עובדים ומעלה. אלו שני תפקידים נפרדים עם אחריות שונה."
  },
  {
    question: "מה הקנסות על אי עמידה בתקנות?",
    answer: "קנסות יכולים להגיע למאות אלפי שקלים ואף לסגירת האתר. במקרים חמורים יש גם אחריות פלילית אישית על בעלי התפקידים. מפקח עבודה רשאי להורות על הפסקת עבודות עד לתיקון הליקויים."
  }
];

export default function ConstructionSafetyOfficer() {
  return (
    <Layout>
      <Helmet>
        <title>ממונה בטיחות באתרי בנייה | Adam Safety</title>
        <meta name="description" content="שירותי ממונה בטיחות מתמחים לאתרי בנייה - עבודה בגובה, ציוד מכני, חפירות ועמידה בתקנות. ליווי מקצועי לכל שלבי הפרויקט." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <HardHat className="w-5 h-5" />
              <span className="text-sm font-medium">התמחות בבנייה</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              ממונה בטיחות באתרי בנייה
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              ליווי מקצועי לאתרי בנייה בכל הגדלים - 
              מיתגים לבנייה רוויה, עם מומחיות בסיכונים הייחודיים של ענף הבנייה
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  ייעוץ לאתרי בנייה
                </Button>
              </Link>
              <Link to="/services/industry-safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  ממונה בתעשייה
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Construction Risks */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סיכונים ייחודיים באתרי בנייה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              ענף הבנייה הוא מהמסוכנים בישראל - הכרת הסיכונים חיונית למניעתם
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {constructionRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="icon-container mb-4">
                  <risk.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{risk.title}</h3>
                <p className="text-muted-foreground mb-4">{risk.description}</p>
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-accent font-medium mb-2">דרישות:</p>
                  <ul className="space-y-1">
                    {risk.regulations.map((reg, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-accent" />
                        {reg}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              השירותים שלנו לאתרי בנייה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              פתרון מקיף לכל צרכי הבטיחות באתר הבנייה שלכם
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {constructionServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground"
              >
                <h3 className="text-lg font-bold mb-2">{service.title}</h3>
                <p className="text-primary-foreground/80 text-sm mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-4 h-4 text-accent" />
                      <span className="text-primary-foreground/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              דרישות החוק לאתרי בנייה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              חובות רגולטוריות שכל אתר בנייה חייב לעמוד בהן
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {legalRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-start gap-4 card-glass p-5 ${req.critical ? 'border-2 border-accent/50' : ''}`}
              >
                <div className={`w-10 h-10 rounded-full ${req.critical ? 'bg-accent/30' : 'bg-accent/20'} flex items-center justify-center flex-shrink-0`}>
                  <req.icon className={`w-5 h-5 ${req.critical ? 'text-accent' : 'text-accent'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-foreground">{req.title}</h3>
                    {req.critical && (
                      <span className="text-xs px-2 py-0.5 bg-accent/20 text-accent rounded-full">חובה קריטית</span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm">{req.details}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 max-w-4xl mx-auto"
          >
            <div className="bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
              <p className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>שימו לב:</strong> המידע מבוסס על תקנות ממונים על הבטיחות התשנ"ו-1996 וחוק ארגון הפיקוח על העבודה (כולל תיקון 11+12).
                  לבדיקת הדרישות הספציפיות לאתר שלכם, פנו אלינו לייעוץ.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              יש לכם פרויקט בנייה?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              אנחנו נדאג שהאתר יעמוד בכל דרישות החוק ושהעובדים יחזרו הביתה בשלום
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  קבלו הצעה
                </Button>
              </Link>
              <Link to="/services/safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  מידע כללי על ממונה
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
